import http, { AxiosRequestConfig, AxiosResponse } from 'axios';
import sha256 from 'crypto-js/sha256';
import hmacSHA256 from 'crypto-js/hmac-sha256';
import Base64 from 'crypto-js/enc-base64';
import Cookies from 'js-cookie';
import Toast from '../components/Toast';
// eslint-disable-next-line import/no-cycle

export const request = http.create({
  baseURL: process.env.NEXT_PUBLIC_API_HOST,
  timeout: 30e3,
});

export const removeToastRequest = http.create({
  baseURL: process.env.NEXT_PUBLIC_API_HOST,
  timeout: 30e3,
});

export const studioRequest = http.create({
  baseURL: process.env.NEXT_PUBLIC_STUDIO_API_HOST,
  timeout: 30e3,
});
export const requestEdm = http.create({
  baseURL: process.env.NEXT_PUBLIC_API_HOST,
  timeout: 30e3,
});

export const gatewayRequest = http.create({
  baseURL: process.env.NEXT_PUBLIC_GATEWAY_API_HOST,
  timeout: 30e3,
});

export const WKGatewayRequest = http.create({
  baseURL: process.env.NEXT_PUBLIC_WK_GATEWAY_API_HOST,
  timeout: 30e3,
});

export const gatewayRsRequest = http.create({
  baseURL: process.env.NEXT_PUBLIC_GATEWAY_RS_API_HOST,
  timeout: 30e3,
});

export const requestStu = http.create({
  baseURL: process.env.NEXT_PUBLIC_STU_CENTER_API_HOST,
  timeout: 30e3,
});

export const requestCRM = http.create({
  baseURL: process.env.NEXT_PUBLIC_CRM_API_HOST,
  timeout: 30e3,
});

declare module 'axios' {
  interface AxiosRequestConfig {
    skipErrorHandler?: boolean;
    skipTransformResponse?: boolean;
    needAuth?: boolean;
    // 是否需要 签名加密 默认不需要
    signature?: boolean;
    // 是否需要 签名加密 时间戳
    timestamp?: number;
    // 是否返回错误信息, 需与skipErrorHandler配合使用
    // skipErrorHandler为true且returnErrorMsg也为true才有效
    returnErrorMsg?: boolean;
    // 是否需要从cookie中获取_Wk_gatherer_sid作为x-sid header
    needSid?: boolean;
  }
}

const responseSuccess = (res: AxiosResponse<any>) => {
  const { config } = res || {};
  if (config.skipTransformResponse) {
    return res.data;
  }

  return res.data;
};
const responseFail = (err: any) => {
  const { response, config, message: systemMessage, isAxiosError } = err;
  if (!isAxiosError || !response) {
    Toast.info(systemMessage);
  }
  if (err?.response) {
    const { status, data = {} } = response;
    const { errors, message } = data;
    const errMsg = (errors && errors[Object.keys(errors)[0]]) || message || err.message;

    if ((+status === 422 || +status === 403 || +status === 400) && !config?.skipErrorHandler) {
      const clearError = errMsg || '未知错误';
      Toast.info(clearError);
      throw new Error(`[Server Response Error]: ${clearError}`);
    }

    if (+status !== 401 && !config?.skipErrorHandler && (typeof data === 'string' || errMsg)) {
      Toast.info(errMsg);
    }

    if (config?.returnErrorMsg && config?.skipErrorHandler) {
      const errorMsg = errMsg || '未知错误';
      Toast.info(errorMsg);
      return { errorMsg };
    }
  }

  throw err;
};

// 设置 error handler
request.interceptors.response.use(
  (res) => {
    return responseSuccess(res);
  },
  (err) => {
    return responseFail(err);
  },
);

request.interceptors.request.use((requestConfig) => {
  const { headers, signature = false, data, url, method, timestamp, needSid = false } = requestConfig;
  if (localStorage.getItem('Accept-Language')) {
    headers.common['Accept-Language'] = localStorage.getItem('Accept-Language') === 'en' ? 'en-US' : 'zh-CN';
  }
  if (signature && timestamp) {
    // TODO 支持 请求头参数自定义
    const bodyHash = Base64.stringify(sha256(JSON.stringify(data)));
    const methodNew = method?.toUpperCase();
    const request = `${methodNew}\n${url}\n\ntimestamp:${timestamp}\n\ntimestamp\n${bodyHash}`;
    const signature = Base64.stringify(hmacSHA256(request, process.env.NEXT_PUBLIC_CLUE_S || ''));
    headers.common.signature = signature;
    headers.common.SignedHeaders = 'timestamp';
    headers.common.timestamp = timestamp;
  }
  // 处理needSid参数，从cookie中获取_Wk_gatherer_sid作为x-sid header
  if (needSid) {
    const sid = Cookies.get('_Wk_gatherer_sid');
    if (sid) {
      headers.common['x-sid'] = sid;
    }
  }
  return requestConfig;
});

// 设置 Edm 不提示 Toast
requestEdm.interceptors.response.use(
  (res) => {
    return responseSuccess(res);
  },
  (err) => {
    return err;
  },
);

removeToastRequest.interceptors.response.use(
  (res) => {
    return responseSuccess(res);
  },
  (err) => {
    throw err;
  },
);

gatewayRequest.interceptors.request.use((requestConfig) => {
  const { needAuth = false, headers } = requestConfig;
  if (needAuth) {
    const token = localStorage.getItem('authToken');
    headers.common.Authorization = `Bearer ${token}`;
  }
  return requestConfig;
});

gatewayRequest.interceptors.response.use(
  (res) => {
    return responseSuccess(res);
  },
  (err) => {
    return responseFail(err);
  },
);

studioRequest.interceptors.request.use((requestConfig) => {
  const { needAuth = false, headers } = requestConfig;
  if (needAuth) {
    const token = localStorage.getItem('authToken');
    headers.common.Authorization = `Bearer ${token}`;
  }
  return requestConfig;
});

studioRequest.interceptors.response.use(
  (res) => {
    return responseSuccess(res);
  },
  (err) => {
    return responseFail(err);
  },
);

gatewayRsRequest.interceptors.request.use((requestConfig) => {
  const { needAuth = false, headers } = requestConfig;
  if (needAuth && !headers.common.Authorization) {
    const token = localStorage.getItem('authToken');
    headers.common.Authorization = `Bearer ${token}`;
  }
  const isEnLang = window.location.href.includes('/en/');
  if (isEnLang) {
    headers.common['Accept-Language'] = 'en-US';
  } else if (localStorage.getItem('Accept-Language')) {
    headers.common['Accept-Language'] = localStorage.getItem('Accept-Language') === 'en' ? 'en-US' : 'zh-CN';
  } else {
    headers.common['Accept-Language'] = 'zh-CN';
  }
  return requestConfig;
});

gatewayRsRequest.interceptors.response.use(
  (res) => {
    return responseSuccess(res);
  },
  (err) => {
    return responseFail(err);
  },
);

requestStu.interceptors.request.use((requestConfig) => {
  const { needAuth = false, headers } = requestConfig;
  if (needAuth && !headers.common.Authorization) {
    const token = localStorage.getItem('authToken');
    headers.common.Authorization = `Bearer ${token}`;
  }
  return requestConfig;
});

requestStu.interceptors.response.use(
  (res) => {
    return responseSuccess(res);
  },
  (err) => {
    return responseFail(err);
  },
);

requestCRM.interceptors.request.use((requestConfig) => {
  const { needAuth = false, headers } = requestConfig;
  if (needAuth) {
    const token = localStorage.getItem('authToken');
    headers.common.Authorization = `Bearer ${token}`;
  }
  return requestConfig;
});

requestCRM.interceptors.response.use(
  (res) => {
    return responseSuccess(res);
  },
  (err) => {
    return responseFail(err);
  },
);

export const generateRequest = (config: AxiosRequestConfig) => {
  const result = http.create({
    baseURL: process.env.NEXT_PUBLIC_API_HOST,
    timeout: 30e3,
    ...config,
  });

  // 设置 error handler
  result.interceptors.response.use(
    (res) => {
      return responseSuccess(res);
    },
    (err) => {
      return responseFail(err);
    },
  );

  return result;
};
